import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Building, AlertCircle, CheckCircle, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PendingClaim {
  id: string;
  nursing_home_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  notes?: string;
  nursing_home?: {
    center_name: string;
    town: string;
    county: string;
    county_slug: string;
    nh_slug: string;
  };
}

interface PendingClaimsProps {
  claims: PendingClaim[];
  isLoading: boolean;
}

export function PendingClaims({ claims, isLoading }: PendingClaimsProps) {
  if (isLoading || !claims || claims.length === 0) {
    return null;
  }

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
      case 'pending_review':
        return {
          label: 'Under Review',
          color: 'bg-yellow-100 text-yellow-800',
          icon: Clock,
          description: 'Your claim is being reviewed by our team. We\'ll contact you within 2-3 business days.'
        };
      case 'pending_call':
        return {
          label: 'Awaiting Call',
          color: 'bg-blue-100 text-blue-800',
          icon: Phone,
          description: 'Our team needs to verify your details via phone call. Please ensure your contact information is up to date.'
        };
      case 'pending_documents':
        return {
          label: 'Documents Required',
          color: 'bg-orange-100 text-orange-800',
          icon: AlertCircle,
          description: 'Additional documentation is required to verify your ownership. Check your email for details.'
        };
      default:
        return {
          label: 'In Progress',
          color: 'bg-gray-100 text-gray-800',
          icon: Clock,
          description: 'Your claim is being processed.'
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} days ago`;
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-600" />
          Pending Claims
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Claims awaiting verification and approval
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {claims.map((claim) => {
            const statusInfo = getStatusInfo(claim.status);
            const StatusIcon = statusInfo.icon;
            
            return (
              <div key={claim.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {claim.nursing_home?.center_name || 'Nursing Home'}
                    </h3>
                    {claim.nursing_home && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {claim.nursing_home.town}, {claim.nursing_home.county}
                      </p>
                    )}
                  </div>
                  <Badge className={statusInfo.color} variant="secondary">
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusInfo.label}
                  </Badge>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-700">
                    {statusInfo.description}
                  </p>
                </div>
                
                <div className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>Submitted: {formatDate(claim.created_at)}</span>
                  <span>Last updated: {getTimeAgo(claim.updated_at)}</span>
                </div>
                
                {claim.notes && (
                  <div className="mt-3 p-2 bg-blue-50 border-l-4 border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Note from our team:</strong> {claim.notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}